import React from 'react';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

interface DeviceGateBannerProps {
    /** The name of the tool being gated */
    toolName: string;
    /** Minimum device class needed */
    minimumDevice: 'tablet' | 'desktop';
    /** Callback when user chooses to proceed anyway */
    onBypass?: () => void;
}

/**
 * Full-screen banner shown when a tool isn't supported on the current device.
 * Designed to be clear and non-judgmental — users may have legitimate reasons
 * to want to view the tool anyway.
 */
export const DeviceGateBanner: React.FC<DeviceGateBannerProps> = ({
    toolName,
    minimumDevice,
    onBypass,
}) => {
    const deviceLabel = minimumDevice === 'desktop' ? 'desktop or laptop' : 'tablet or desktop';

    return (
        <div
            className="flex h-full flex-col items-center justify-center px-6 py-12 text-center"
            data-testid="device-gate-banner"
        >
            <div className="max-w-sm space-y-6">
                {/* Icon */}
                <div className="flex justify-center">
                    <Icon
                        name={minimumDevice === 'desktop' ? 'desktop_windows' : 'tablet'}
                        className="text-5xl text-gray-300 dark:text-gray-600"
                    />
                </div>

                {/* Title */}
                <h2 className="font-bold text-gray-900 text-xl dark:text-gray-100">
                    {toolName} works best on a {deviceLabel}
                </h2>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed dark:text-gray-400">
                    This tool uses detailed layouts and precision interactions that need
                    more screen space. For the best experience, switch to a{' '}
                    {minimumDevice === 'desktop' ? 'laptop or desktop computer' : 'tablet, laptop, or desktop'}.
                </p>

                {/* Bypass option */}
                {onBypass ? <NativeButton
                        type="button"
                        onClick={onBypass}
                        className="mx-auto flex min-h-[44px] items-center justify-center text-gray-400 text-xs underline underline-offset-2 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                        Continue anyway →
                    </NativeButton> : null}
            </div>
        </div>
    );
};
